import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapPin, Search } from 'lucide-react';
import Input from '../Input';
import styles from './MapPicker.module.scss';

/* ────────────────────────────────────────────────────────────
 * MapPicker — address search + draggable map marker + reverse
 * geocoding, in one self-contained primitive.
 *
 * Replaces the hand-rolled storeMap.js pattern (Leaflet + raw
 * input + ad-hoc suggestion list + raw coord readout) that
 * shipped inside AddNewStore.
 *
 *   <MapPicker
 *     id="addnewstore-map"
 *     value={{
 *       lat: AddStore.shop_latitude,
 *       lng: AddStore.shop_longitude,
 *       address: AddStore.shop_address,
 *     }}
 *     onChange={({ lat, lng, address, landmark, city, state, country }) => {
 *       setAddStore(prev => ({
 *         ...prev,
 *         shop_latitude: lat,
 *         shop_longitude: lng,
 *         shop_address: address,
 *         shop_landmark: landmark,
 *         shop_city: city,
 *         shop_state: state,
 *         shop_country: country,
 *       }));
 *     }}
 *     markerIconUrl={mapMarker}
 *   />
 *
 * The autocomplete + reverse-geocode endpoints hit the public
 * Nominatim service (https://nominatim.openstreetmap.org) — same
 * source the legacy storeMap.js used. No app/API/auth context.
 * ──────────────────────────────────────────────────────────── */

const DEFAULT_POSITION = [10.8505, 76.2711];
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';

const FlyTo = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 13, { animate: true, duration: 1.5 });
    }
  }, [position, map]);
  return null;
};

const extractLocationDetails = (data) => {
  const display = data?.display_name || '';
  const address = data?.address || {};
  return {
    address: display || 'Unknown address',
    landmark: address.neighbourhood || address.suburb || '',
    city: address.city || address.town || address.village || '',
    state: address.state || '',
    country: address.country || '',
  };
};

const MapPicker = ({
  id,
  value,
  onChange,
  markerIconUrl,
  searchPlaceholder,
  error,
  minSearchChars,
  searchDebounceMs,
  disabled,
  className,
}) => {
  const lat = Number(value?.lat) || DEFAULT_POSITION[0];
  const lng = Number(value?.lng) || DEFAULT_POSITION[1];
  const markerPosition = [lat, lng];

  const [searchValue, setSearchValue] = useState(value?.address || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionSelected, setIsSuggestionSelected] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (value?.address) setSearchValue(value.address);
  }, [value?.address]);

  const markerIcon = useMemo(() => {
    if (!markerIconUrl) return undefined;
    return new L.Icon({
      iconUrl: markerIconUrl,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    });
  }, [markerIconUrl]);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < minSearchChars) {
      setSuggestions([]);
      return;
    }
    try {
      const url = `${NOMINATIM_BASE}/search?format=json&q=${encodeURIComponent(
        query
      )}`;
      const response = await fetch(url);
      const data = await response.json();
      setSuggestions(
        (data || []).map((item) => ({
          name: item.display_name,
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
        }))
      );
    } catch (err) {
      // Network failures are expected (Nominatim rate-limits). Silently
      // clear suggestions instead of surfacing a toast — the legacy code
      // did the same and the user can always drag the pin.
      setSuggestions([]);
    }
  }, [minSearchChars]);

  const scheduleFetch = useCallback(
    (query) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(query);
      }, searchDebounceMs);
    },
    [fetchSuggestions, searchDebounceMs]
  );

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const reverseGeocode = useCallback(async (latVal, lngVal) => {
    try {
      const res = await fetch(
        `${NOMINATIM_BASE}/reverse?format=json&lat=${latVal}&lon=${lngVal}`
      );
      const data = await res.json();
      return extractLocationDetails(data);
    } catch (err) {
      return null;
    }
  }, []);

  const emitChange = (next) => {
    if (!onChange) return;
    onChange({
      lat: next.lat,
      lng: next.lng,
      address: next.address ?? value?.address ?? '',
      landmark: next.landmark ?? value?.landmark ?? '',
      city: next.city ?? value?.city ?? '',
      state: next.state ?? value?.state ?? '',
      country: next.country ?? value?.country ?? '',
    });
  };

  const handleInputChange = (e) => {
    const next = e.target.value;
    setSearchValue(next);
    setIsSuggestionSelected(false);
    emitChange({ lat, lng, address: next });
    if (next.length >= minSearchChars) {
      scheduleFetch(next);
    } else {
      setSuggestions([]);
    }
  };

  const handleInputFocus = () => {
    if (!isSuggestionSelected && searchValue.length >= minSearchChars) {
      fetchSuggestions(searchValue);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setSuggestions([]), 200);
  };

  const handleSelectSuggestion = async (item) => {
    setSearchValue(item.name);
    setSuggestions([]);
    setIsSuggestionSelected(true);
    const nextLat = Number(item.lat.toFixed(6));
    const nextLng = Number(item.lng.toFixed(6));
    const details = await reverseGeocode(nextLat, nextLng);
    emitChange({
      lat: nextLat,
      lng: nextLng,
      address: details?.address || item.name,
      landmark: details?.landmark || '',
      city: details?.city || '',
      state: details?.state || '',
      country: details?.country || '',
    });
  };

  const handleMarkerDragEnd = async (event) => {
    const { lat: nextLat, lng: nextLng } = event.target.getLatLng();
    const roundedLat = Number(nextLat.toFixed(6));
    const roundedLng = Number(nextLng.toFixed(6));
    const details = await reverseGeocode(roundedLat, roundedLng);
    setSearchValue(details?.address || '');
    setIsSuggestionSelected(true);
    emitChange({
      lat: roundedLat,
      lng: roundedLng,
      address: details?.address || '',
      landmark: details?.landmark || '',
      city: details?.city || '',
      state: details?.state || '',
      country: details?.country || '',
    });
  };

  const showSuggestions =
    !disabled && !isSuggestionSelected && searchValue !== '' && suggestions.length > 0;

  return (
    <div className={[styles.wrapper, className || ''].filter(Boolean).join(' ')}>
      <div className={styles.searchWrap}>
        <Input
          id={id}
          value={searchValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={searchPlaceholder}
          leftIcon={<Search size={16} />}
          disabled={disabled}
          error={error}
          autoComplete="off"
        />

        {showSuggestions && (
          <ul className={styles.suggestions} role="listbox">
            {suggestions.map((item, index) => (
              <li
                key={`${item.lat}-${item.lng}-${index}`}
                role="option"
                aria-selected={false}
                className={styles.suggestion}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectSuggestion(item);
                }}
              >
                {item.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.mapFrame}>
        <MapContainer
          center={markerPosition}
          zoom={13}
          className={styles.map}
        >
          <FlyTo position={markerPosition} />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <Marker
            position={markerPosition}
            draggable={!disabled}
            icon={markerIcon}
            eventHandlers={!disabled ? { dragend: handleMarkerDragEnd } : undefined}
          >
            <Popup>Drag the marker or type an address.</Popup>
          </Marker>
        </MapContainer>
      </div>

      <div className={styles.coords}>
        <span className={styles.coordPill}>
          <MapPin size={12} strokeWidth={1.75} aria-hidden="true" />
          <span className={styles.coordLabel}>Lat</span>
          <span className={styles.coordValue}>{markerPosition[0]}</span>
        </span>
        <span className={styles.coordPill}>
          <MapPin size={12} strokeWidth={1.75} aria-hidden="true" />
          <span className={styles.coordLabel}>Lng</span>
          <span className={styles.coordValue}>{markerPosition[1]}</span>
        </span>
      </div>
    </div>
  );
};

MapPicker.propTypes = {
  id: PropTypes.string,
  value: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    address: PropTypes.string,
    landmark: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  markerIconUrl: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  error: PropTypes.string,
  minSearchChars: PropTypes.number,
  searchDebounceMs: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

MapPicker.defaultProps = {
  id: undefined,
  value: null,
  markerIconUrl: '',
  searchPlaceholder: 'Search for an address…',
  error: '',
  minSearchChars: 3,
  searchDebounceMs: 500,
  disabled: false,
  className: '',
};

export default MapPicker;
